import type { LandingLiveContest, LandingOutcome } from "./feed";
import { estimatePayoutCents } from "./payout";

// The earnings calculator's math. Everything is integer cents and every
// payout goes through estimatePayoutCents, the slip's mirror of
// models.ComputeSettlement, so the calculator and the slip never disagree.

// The most a contest pays its winning producer (services.MarketMaxProducerBps).
export const MAX_PRODUCER_BPS = 2000;
// The service's stake bounds (services.DefaultMinStakeCents and
// DefaultPerUserCapCents), for a feed row that does not carry its own.
const DEFAULT_MIN_STAKE_CENTS = 100;
const DEFAULT_STAKE_CAP_CENTS = 100_000;
// Starting assumptions when no open battle has a pot or a backer yet. The
// visitor can change both; they are labelled as assumptions, not live data.
export const FALLBACK_POT_CENTS = 50_000;
export const FALLBACK_BACK_CENTS = 1_000;

const STAKE_STEP_CENTS = 100;
const MAX_LEGS = 3;

export function isOpen(contest: LandingLiveContest, now = Date.now()): boolean {
  return new Date(contest.ends_at).getTime() > now;
}

// A battle tagged "Any" takes a beat in any genre, so it is not a genre of
// its own and it matches every genre filter.
const isAnyGenre = (genre: string) => genre.toLowerCase() === "any";

// The genres open battles are in, for the picker. Empty when none are tagged.
export function genresOf(contests: LandingLiveContest[], now = Date.now()): string[] {
  const seen = new Set<string>();
  for (const c of contests) {
    const genre = c.genre?.trim() ?? "";
    if (genre && !isAnyGenre(genre) && isOpen(c, now)) seen.add(genre);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

// Battles still open, narrowed to a genre ("" keeps every genre).
export function openInGenre(contests: LandingLiveContest[], genre: string, now = Date.now()): LandingLiveContest[] {
  return contests.filter((c) => {
    if (!isOpen(c, now)) return false;
    const own = c.genre?.trim() ?? "";
    return genre === "" || own === genre || isAnyGenre(own);
  });
}

// What fans have staked, without the house seed.
export const stakedCents = (c: LandingLiveContest) => c.pool_cents - (c.seed_cents ?? 0);

const wholeDollars = (cents: number) => Math.max(100, Math.round(cents / 100) * 100);

export type LiveDefaults = {
  cutBps: number;
  // False when no open battle carries a producer cut and the cut shown is
  // the most a battle can pay.
  cutFromLive: boolean;
  potCents: number;
  backCents: number;
};

// What the producer calculator starts from: the average producer cut, pot,
// and fan back across the battles given, or the fallbacks where none exist.
export function liveDefaults(contests: LandingLiveContest[]): LiveDefaults {
  const cuts = contests.map((c) => c.producer_bps ?? 0).filter((bps) => bps > 0);
  // The producer's cut is taken from stakes; the house seed goes to fans.
  const pots = contests.map(stakedCents).filter((p) => p > 0);
  let staked = 0;
  let backers = 0;
  for (const c of contests) {
    for (const o of c.outcomes) {
      staked += o.staked_cents ?? 0;
      backers += o.backer_count;
    }
  }
  const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
  return {
    cutBps: cuts.length ? Math.round(mean(cuts)) : MAX_PRODUCER_BPS,
    cutFromLive: cuts.length > 0,
    potCents: pots.length ? wholeDollars(mean(pots)) : FALLBACK_POT_CENTS,
    backCents: backers > 0 && staked > 0 ? wholeDollars(staked / backers) : FALLBACK_BACK_CENTS,
  };
}

export type ProducerPlan = {
  // The pots a producer has to win for their cut to reach the goal.
  potsToWinCents: number;
  wins: number;
  fansPerPot: number;
};

// producerPlan works the goal backwards: the cut is a share of the whole pot,
// so the pots to win are goal ÷ cut; the wins are those pots ÷ one pot; the
// fans per pot are one pot ÷ one fan's back. Null when any input is not
// positive, since there is no honest answer to divide by zero.
export function producerPlan(goalCents: number, cutBps: number, potCents: number, backCents: number): ProducerPlan | null {
  if (goalCents <= 0 || cutBps <= 0 || potCents <= 0 || backCents <= 0) return null;
  const potsToWinCents = Math.ceil((goalCents * 10_000) / cutBps);
  return {
    potsToWinCents,
    wins: Math.ceil(potsToWinCents / potCents),
    fansPerPot: Math.ceil(potCents / backCents),
  };
}

export type FanLeg = {
  contest: LandingLiveContest;
  outcome: LandingOutcome;
  stakeCents: number;
  // What the slip would estimate paying back, stake included.
  payoutCents: number;
};

function stakeBounds(contest: LandingLiveContest): { first: number; cap: number } {
  const min = Math.max(contest.min_stake_cents ?? DEFAULT_MIN_STAKE_CENTS, STAKE_STEP_CENTS);
  return {
    first: Math.ceil(min / STAKE_STEP_CENTS) * STAKE_STEP_CENTS,
    cap: contest.per_user_cap_cents ?? DEFAULT_STAKE_CAP_CENTS,
  };
}

// legFor finds the smallest whole-dollar stake within the market's bounds
// that profits at least profitCents if this pick wins. Profit rises with the
// stake and then falls (a big stake mostly wins its own money back), so this
// walks up rather than bisecting. Null when no allowed stake gets there.
export function legFor(contest: LandingLiveContest, outcome: LandingOutcome, profitCents: number): FanLeg | null {
  if (profitCents <= 0) return null;
  const { first, cap } = stakeBounds(contest);
  for (let stake = first; stake <= cap; stake += STAKE_STEP_CENTS) {
    const payout = estimatePayoutCents(
      contest.pool_cents,
      contest.take_bps ?? 0,
      outcome.staked_cents ?? 0,
      stake,
      contest.seed_cents ?? 0,
    );
    if (payout === null) return null;
    if (payout - stake >= profitCents) return { contest, outcome, stakeCents: stake, payoutCents: payout };
  }
  return null;
}

// The single pick and stake that profit the most if they win, across every
// battle given. A fan's profit is bounded by the credits on the other picks,
// so when no pick reaches the goal this is the honest ceiling to show.
// Null when no stake profits at all.
export function bestPick(contests: LandingLiveContest[]): FanLeg | null {
  let best: FanLeg | null = null;
  for (const contest of contests) {
    const { first, cap } = stakeBounds(contest);
    for (const outcome of contest.outcomes) {
      for (let stake = first; stake <= cap; stake += STAKE_STEP_CENTS) {
        const payout = estimatePayoutCents(
          contest.pool_cents,
          contest.take_bps ?? 0,
          outcome.staked_cents ?? 0,
          stake,
          contest.seed_cents ?? 0,
        );
        if (payout === null) break;
        if (payout - stake > (best ? best.payoutCents - best.stakeCents : 0)) {
          best = { contest, outcome, stakeCents: stake, payoutCents: payout };
        }
      }
    }
  }
  return best;
}

// The pick in a contest the crowd backs most that still reaches the profit,
// so a fan sees the likeliest route first rather than the longest shot.
function bestLeg(contest: LandingLiveContest, profitCents: number): FanLeg | null {
  const byShare = [...contest.outcomes].sort((a, b) => b.share_bps - a.share_bps);
  for (const outcome of byShare) {
    const leg = legFor(contest, outcome, profitCents);
    if (leg) return leg;
  }
  return null;
}

// Single picks that each reach the goal on their own, crowd favourites first.
export function singlePicks(contests: LandingLiveContest[], goalCents: number, limit = 3): FanLeg[] {
  const legs: FanLeg[] = [];
  for (const contest of contests) {
    for (const outcome of contest.outcomes) {
      const leg = legFor(contest, outcome, goalCents);
      if (leg) legs.push(leg);
    }
  }
  legs.sort((a, b) => b.outcome.share_bps - a.outcome.share_bps || a.stakeCents - b.stakeCents);
  return legs.slice(0, limit);
}

// A stack splits the goal across picks in different contests, each profiting
// its share. Every leg has to win. Tries three legs, then two; null when the
// battles cannot carry even two.
export function stackPicks(contests: LandingLiveContest[], goalCents: number): FanLeg[] | null {
  if (goalCents <= 0) return null;
  for (let legs = Math.min(MAX_LEGS, contests.length); legs >= 2; legs--) {
    const target = Math.ceil(goalCents / legs);
    const found = contests
      .map((c) => bestLeg(c, target))
      .filter((leg): leg is FanLeg => leg !== null)
      .sort((a, b) => b.outcome.share_bps - a.outcome.share_bps);
    if (found.length >= legs) return found.slice(0, legs);
  }
  return null;
}
